import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../../api/GetActiveScalesApi";
import { GetAllPlanGroupsApi } from "../../../api/GetAllPlanGroups";
import GetScaleUnitsByScaleApi from "../../../api/GetScaleUnitsByScaleApi";
import { GetTreeSelectUnitsApi } from "../../../api/GetTreeSelectUnitsApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import moment from "moment";
import EditTopPlanApi from "../../../api/EditTopPlanApi";
import { GetPerfCycleTopLevelApi } from "../../../api/perf-cycles/GetPerfCycleTopPlanApi";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import PageLoader from "../../sub/PageLoader";
import { GetApproversApi } from "../../../api/approvals/GetApproversApi";
import GetDropdownOptionsByIdsApi from "../../../api/attributes/GetDropdownOptionsByIdsApi";

const { Option } = Select;

const { TextArea } = Input;

function EditTopPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [unitsTree, setUnitsTree] = useState([]);
  const [topPlan, setTopPlan] = useState();
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [unitPositions, setUnitPositions] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const [form] = Form.useForm();
  const [valuesChanged, setValuesChanged] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const [approvers, setApprovers] = useState([]);

  function handleCancel() {
    setSubmit(false);
    setUnitsTree([]);
    setPlanGroups([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    setTopPlan();
    form.resetFields();
    props.handleCancel();
    setValuesChanged(false);
  }

  useEffect(() => {
    if (props.visible) {
      getTopPlanDetails();
      getUnitsTree();
      getPlanGroups();
      getActiveScales();
      getUnitPositions();
      getApprovers();
      form.setFields([
        {
          name: "title",
          value: props.editPlan?.title,
        },
        {
          name: "description",
          value: props.editPlan?.description,
        },

        {
          name: "target",
          value: props.editPlan?.target,
        },
        {
          name: "unitScale",
          value: props.editPlan?.scale?.scaleUnitId,
        },
        {
          name: "targetDate",
          value: props.editPlan?.targetDate
            ? moment(props.editPlan?.targetDate)
            : "",
        },
      ]);
    }
  }, [props.visible]);

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      const planData = apiResponse.data;

      const dropdownAttrIds = (planData?.planLevelAttributes || [])
        .filter((a) => a.valueType === "dropdown")
        .map((a) => a.id);

      if (dropdownAttrIds.length > 0) {
        const optionsRes = await GetDropdownOptionsByIdsApi(dropdownAttrIds);
        if (optionsRes.success && optionsRes.data) {
          planData.planLevelAttributes = planData.planLevelAttributes.map((attr) => {
            if (attr.valueType === "dropdown") {
              return { ...attr, dropdownOptions: optionsRes.data[attr.id] || [] };
            }
            return attr;
          });
        }
      }

      setTopPlan(planData);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);

      form.setFields([
        {
          name: "unitId",
          value: props.editPlan?.unit?.unitId,
        },
      ]);
    }
  }

  async function getPlanGroups() {
    const apiResponse = await GetAllPlanGroupsApi();
    if (apiResponse.message === "success") {
      setPlanGroups(apiResponse.planGroups);
      form.setFields([
        {
          name: "planGroupId",
          value: props.editPlan?.planGroupId,
        },
      ]);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
      selectScale(props.editPlan?.scale?.scaleId);
      form.setFields([
        {
          name: "targetScale",
          value: props.editPlan?.scale?.scaleId,
        },
      ]);
    }
  }

  async function getUnitPositions() {
    const apiResponse = await GetUnitPositionsApi2(
      props.editPlan?.unit?.unitId
    );
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: props.editPlan?.assignedTo?.positionId,
        },
      ]);
    }
  }

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
      form.setFields([
        {
          name: "approver",
          value: apiResponse?.data[0]?.id || "",
        },
      ]);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
  }

  function selectPlanGroup(value) {
    planGroups.forEach((group) => {
      if (value === group.id) {
        form.setFields([
          {
            name: "targetScale",
            value: group.scale?.id,
          },
        ]);
        selectScale(group.scale?.id);
      }
    });
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];

    topPlan?.planLevelAttributes?.forEach((attribute) => {
      let value = values[attribute.id];
      // Convert array to comma-separated string for dropdown values
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      attributesValues.push({
        attributeId: attribute.id,
        value: value,
      });
    });

    const apiResponse = await EditTopPlanApi(
      props.editPlan?.planId,
      values.title,
      values.description || "",
      values.target,
      values.targetDate || "",
      values.assignTo,
      positionState?.id,
      values.approver || null,
      attributesValues
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "Plan updated successfully") {
      openNotificationSuccess("bottomRight", `${topPlanState} edited`);
      props.handleOk(apiResponse.data);
    } else if (apiResponse.message === "Plan update submitted for approval") {
      openNotificationSuccess(
        "bottomRight",
        `${topPlanState} update submitted for approval`
      );
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit plan. Try again later."
      );
    }
    handleCancel();
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }} disabled>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    // Only render dropdown and boolean attributes
    const dropdownAndBooleanAttributes = topPlan?.planLevelAttributes
      ?.filter((attribute) => {
        return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
      })
      ?.sort((a, b) => a.name.localeCompare(b.name)) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
        let planAttribute = props.editPlan?.planAttributeValue?.find(
          (item) => item.planAttributeId === attribute.id
        );

        if (planAttribute) {
          output.push(
            <Col span={12} key={attribute.id}>
              <Form.Item label={planAttribute.name} name={attribute.id}>
                {planAttribute.valueType === "dropdown" ? (
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={`Select ${planAttribute.name}`}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {attribute.dropdownOptions?.map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.value}
                      </Option>
                    ))}
                  </Select>
                ) : planAttribute.valueType === "boolean" ? (
                  <Select
                    placeholder={`Select ${planAttribute.name}`}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <Option value={true}>Yes</Option>
                    <Option value={false}>No</Option>
                  </Select>
                ) : null}
              </Form.Item>
            </Col>
          );
        } else {
          output.push(
            <Col span={12} key={attribute.id}>
              <Form.Item label={attribute.name} name={attribute.id}>
                {attribute.valueType === "dropdown" ? (
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={`Select ${attribute.name}`}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {attribute.dropdownOptions?.map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.value}
                      </Option>
                    ))}
                  </Select>
                ) : attribute.valueType === "boolean" ? (
                  <Select
                    placeholder={`Select ${attribute.name}`}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <Option value={true}>Yes</Option>
                    <Option value={false}>No</Option>
                  </Select>
                ) : null}
              </Form.Item>
            </Col>
          );
        }
      });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    // Filter out dropdown, boolean, and date attributes (all date attributes handled separately)
    const otherAttributes = topPlan?.planLevelAttributes
      ?.filter((attribute) => {
        return !(attribute.valueType === "dropdown" || 
                 attribute.valueType === "boolean" || 
                 attribute.valueType === "date");
      })
      ?.sort((a, b) => a.name.localeCompare(b.name)) || [];

    otherAttributes.forEach((attribute) => {
        let planAttribute = props.editPlan?.planAttributeValue?.find(
          (item) => item.planAttributeId === attribute.id
        );

        if (planAttribute) {
          output.push(
            <Col span={12} key={attribute.id}>
              <Form.Item label={planAttribute.name} name={attribute.id}>
                {planAttribute.valueType === "text" ? (
                  <Input className="w-full" placeholder={planAttribute.name} />
                ) : planAttribute.valueType === "number" ? (
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder={planAttribute.name}
                  />
                ) : planAttribute.valueType === "double" ? (
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: "100%" }}
                    placeholder={planAttribute.name}
                  />
                ) : planAttribute.valueType === "date" ? (
                  <DatePicker
                    className="w-full"
                    placeholder={planAttribute.name}
                  />
                ) : null}
              </Form.Item>
            </Col>
          );
        } else {
          output.push(
            <Col span={12} key={attribute.id}>
              <Form.Item label={attribute.name} name={attribute.id}>
                {attribute.valueType === "text" ? (
                  <Input className="w-full" placeholder={attribute.name} />
                ) : attribute.valueType === "number" ? (
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder={attribute.name}
                  />
                ) : attribute.valueType === "double" ? (
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: "100%" }}
                    placeholder={attribute.name}
                  />
                ) : attribute.valueType === "date" ? (
                  <DatePicker className="w-full" placeholder={attribute.name} />
                ) : null}
              </Form.Item>
            </Col>
          );
        }
      });

    // Set form values after a short delay to ensure form is ready
    setTimeout(() => {
      props.editPlan?.planAttributeValue?.forEach((attribute) => {
        let fieldValue = "";
        
        if (attribute.value !== null) {
          if (attribute.valueType === "text") {
            fieldValue = attribute.text;
          } else if (attribute.valueType === "number") {
            fieldValue = attribute.number;
          } else if (attribute.valueType === "double") {
            fieldValue = attribute.double;
          } else if (attribute.valueType === "dropdown") {
            // Parse the dropdown value - could be JSON array or comma-separated
            if (!attribute.dropdown) {
              fieldValue = [];
            } else {
              try {
                fieldValue = JSON.parse(attribute.dropdown);
                // Ensure it's an array
                if (!Array.isArray(fieldValue)) {
                  fieldValue = [fieldValue];
                }
              } catch {
                // If not JSON, split by comma (or single value)
                if (attribute.dropdown.includes(',')) {
                  fieldValue = attribute.dropdown.split(',').map(id => String(id).trim());
                } else {
                  // Single value without comma
                  fieldValue = [String(attribute.dropdown).trim()];
                }
              }
              // Ensure all IDs are strings
              fieldValue = fieldValue.map(id => String(id));
            }
          } else if (attribute.valueType === "date") {
            fieldValue = moment(attribute.date);
          }
        }
        
        // Find the corresponding template attribute to get the correct ID
        const templateAttr = topPlan?.planLevelAttributes?.find(
          (attr) => attr.id === attribute.planAttributeId
        );
        const fieldName = templateAttr?.id || attribute.planAttributeId;
        
        form.setFields([
          {
            name: fieldName,
            value: fieldValue,
          },
        ]);
      });
    }, 100);

    return output;
  }



  return (
    <>
      <Modal
        title={`Edit ${topPlanState}`}
        visible={props.visible}
        onOk={props.handleOk}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <Form
          name="editPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          onValuesChange={() => {
            setValuesChanged(true);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Plan group*"
                name="planGroupId"
                rules={[
                  {
                    required: true,
                    message: "Please select a plan group!",
                  },
                ]}
              >
                <Select
                  disabled
                  showSearch
                  placeholder="Select plan group"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onSelect={(value) => selectPlanGroup(value)}
                >
                  {planGroups.map((group) => {
                    return (
                      <Option key={group.id} value={group.id}>
                        {group.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={`Title`} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unitId"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={unitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  disabled={true}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Target scale*"
                name="targetScale"
                rules={[
                  {
                    required: true,
                    message: "Please select a target scale!",
                  },
                ]}
              >
                <Select placeholder="Select a scale" disabled={true}>
                  {scales.map((scale) => {
                    return (
                      <Option key={scale.id} value={scale.id}>
                        {scale.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Target*"
                name="target"
                rules={[
                  {
                    required: true,
                    message: "Please enter target!",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  addonAfter={addScaleUnit ? unitSelector : percentage && "%"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {topPlan?.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={600}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {renderDropdownAndBooleanAttributes()}

            {topPlan?.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {renderOtherAttributes()}

            {!topPlan && <PageLoader />}

            {positionState?.userRole?.name === "Manager" &&
              !!approvers?.length && (
                <Col span={12}>
                  <Form.Item
                    label="Approver"
                    name="approver"
                    rules={[
                      {
                        required: true,
                        message: "Please select approver!",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select approver"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                    >
                      {approvers?.map((approver) => {
                        return (
                          <Option key={approver?.id} value={approver?.id}>
                            {(approver?.designations?.abbreviation ||
                              approver?.designations?.name) +
                              "," +
                              approver?.units?.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit || !valuesChanged}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default EditTopPlanModal;
